import { Button } from '@material-ui/core';
import { AssetType, getLoanById, Loan } from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { LoanDetailActionsDialog } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/LoanDetailActionsDialog';
import { LOAN_STATUSES_NON_ACTIVE_FOR_DETAIL_ACTIONS } from '@/components/widgets/LoanDetailActions/constants/statusesLists';
import { useOrderRefund } from '@/components/widgets/LoanDetailActions/hooks/useOrderRefund';
import { getAddCoBorrowerRelatedActions } from '@/components/widgets/LoanDetailActions/maps/getAddCoBorrowerRelatedActions';
import { getLoanApplicationRelatedActions } from '@/components/widgets/LoanDetailActions/maps/getLoanApplicationRelatedActions';
import { getLoanCancellationRelatedActions } from '@/components/widgets/LoanDetailActions/maps/getLoanCancellationRelatedActions';
import { getLoanFormalizationRelatedActions } from '@/components/widgets/LoanDetailActions/maps/getLoanFormalizationRelatedActions';
import { getNotificationsRelatedActions } from '@/components/widgets/LoanDetailActions/maps/getNotificationsRelatedActions';
import { getOrderRefundRelatedActions } from '@/components/widgets/LoanDetailActions/maps/getOrderRefundRelatedActions';
import { LoanDetailAction } from '@/components/widgets/LoanDetailActions/types/LoanDetailAction';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { UserEntity } from '@/entities/UserEntity';
import { useCache } from '@/hooks/requests/useCache';

export const LoanDetailActions = () => {
  const { loanId } = useParams();

  if (!loanId) {
    return null;
  }

  const { data: loan } = useCache(getLoanById, { loanId });
  const { userEntity } = useUserContext();

  if (!loan || !userEntity) {
    return null;
  }

  return <LoanDetailActionsComponent loan={loan} userEntity={userEntity} />;
};

export const LoanDetailActionsComponent = ({ loan, userEntity }: { loan: Loan; userEntity: UserEntity }) => {
  const { openRefund, isOrderRefundable, loadingNemuruJs } = useOrderRefund(loan);
  const { opened, setOpened, toggleDialog } = useDialog();

  const [currentAction, setCurrentAction] = useState<LoanDetailAction | undefined>();

  const toggleActionDialog = (actionType: LoanDetailAction) => {
    setCurrentAction(actionType);
    toggleDialog();
  };

  const assetTypeCardLoan = loan.assetReference[0].type === AssetType.CARD;

  const isCurrentLoanInNonActiveStatus = LOAN_STATUSES_NON_ACTIVE_FOR_DETAIL_ACTIONS.some(
    (nonActiveStatus) => loan.status === nonActiveStatus,
  );

  const loanFormalizationRelatedActions = getLoanFormalizationRelatedActions({
    toggleActionDialog,
    role: userEntity.role,
    loan: loan,
  });

  const addCoBorrowerRelatedActions = getAddCoBorrowerRelatedActions({
    toggleActionDialog,
    role: userEntity.role,
    loan: loan,
  });

  const loanCancellationRelatedActions = getLoanCancellationRelatedActions({
    toggleActionDialog,
    role: userEntity.role,
    loan: loan,
  });

  const loanRefundRelatedActions = getOrderRefundRelatedActions({
    openRefund,
    isOrderRefundable,
    loadingNemuruJs,
    role: userEntity.role,
  });

  const loanApplicationRelatedActions = getLoanApplicationRelatedActions({
    toggleActionDialog,
    loan,
    role: userEntity.role,
  });

  const notificationsRelatedActions = getNotificationsRelatedActions({
    toggleActionDialog,
    role: userEntity.role,
    loan: loan,
  });

  const loanDetailsActionsArray = isCurrentLoanInNonActiveStatus
    ? []
    : [
        ...(assetTypeCardLoan ? [] : addCoBorrowerRelatedActions),
        ...(assetTypeCardLoan ? [] : loanApplicationRelatedActions),
        ...notificationsRelatedActions,
        ...loanFormalizationRelatedActions,
        ...loanCancellationRelatedActions,
      ];

  const orderDetailsActionsArray = [
    ...(isCurrentLoanInNonActiveStatus ? [] : loanCancellationRelatedActions),
    ...notificationsRelatedActions,
    ...loanRefundRelatedActions,
  ];

  return (
    <>
      <Button>
        <MoreActionsMenu
          actionsOptionsArray={
            loan.assetReference[0].type === AssetType.ORDER ? orderDetailsActionsArray : loanDetailsActionsArray
          }
        >
          <Font variant={'subtitle2'} color={'textSecondary'}>
            <i
              className={'material-icons-outlined mr-2'}
              style={{ verticalAlign: '-5px', marginLeft: '-4px', fontSize: '1.5em' }}
            >
              menu
            </i>
            <Text tId={'ACTIONS'} />
          </Font>
        </MoreActionsMenu>
      </Button>
      {currentAction && (
        <LoanDetailActionsDialog
          currentAction={currentAction}
          loan={loan}
          useDialogProps={{ opened, setOpened, toggleDialog }}
        />
      )}
    </>
  );
};
